import { useFolders, useNotes, useServices } from "@hq/client-react";
import type { PaneComponentProps } from "@hq/layout";
import { NoteList } from "@hq/notes-ui";
import { Button, EmptyState, toast } from "@z/ui";
import { LayoutTemplate } from "lucide-react";
import { TEMPLATES_FOLDER_NAME, templatesFolderId } from "../templates";
import { useWorkspace } from "../workspace/WorkspaceContext";

/**
 * Templates are notes in a folder named "Templates". "New from template" copies one into a
 * fresh note. No schema, no special entity.
 */
export function TemplatesPane({ pane }: PaneComponentProps) {
  const ws = useWorkspace();
  const services = useServices();
  const { data: folders } = useFolders();
  const folderId = templatesFolderId(folders ?? []);
  const { data: templates } = useNotes(folderId ? { folderId } : { folderId: "__none__" });
  const selectedNoteId =
    typeof pane.params.selectedNoteId === "string" ? pane.params.selectedNoteId : null;

  const createFolder = async () => {
    const folder = await services.folders.create({ name: TEMPLATES_FOLDER_NAME });
    const note = await services.notes.create({
      folderId: folder.id,
      title: "Meeting",
      markdown: "# Meeting\n\n## Attendees\n\n## Notes\n\n## Action items\n",
    });
    ws.openNote(note.id, { pinned: true, autoFocus: true });
  };

  if (!folderId) {
    return (
      <EmptyState
        className="h-full"
        icon={<LayoutTemplate className="size-6" />}
        title="No templates yet"
        description={`Notes in a folder called "${TEMPLATES_FOLDER_NAME}" appear here and in the palette's "New from template".`}
        action={<Button onClick={() => void createFolder()}>Create the Templates folder</Button>}
      />
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex items-center justify-between gap-3 border-b border-border/60 px-6 py-5">
        <h1 className="text-xl font-semibold tracking-tight">Templates</h1>
        <span className="text-fg-faint text-xs">{templates?.length ?? 0} template(s)</span>
      </div>
      <NoteList
        renderAction={(template) => (
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              void services.notes
                .create({
                  title: template.title,
                  markdown: template.markdown,
                  ...(template.tags ? { tags: template.tags } : {}),
                })
                .then((note) => ws.openNote(note.id, { pinned: true, autoFocus: true }))
                .catch(() =>
                  toast({ title: "Could not create note from template", variant: "danger" }),
                );
            }}
          >
            Use template
          </Button>
        )}
        folderId={folderId}
        selectedNoteId={selectedNoteId}
        onSelect={(id) => {
          ws.setParams(pane.id, { ...pane.params, selectedNoteId: id });
          ws.openNote(id);
        }}
        className="min-h-0 flex-1 overflow-y-auto"
      />
    </div>
  );
}
